import { useCallback, useContext, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { CurrentDataContext } from 'src/app/current-data';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useConfigStringList } from 'src/contexts/statsig-config-provider';
import authRequest from 'src/utils/fetch';
import store from 'src/utils/store';

import messages from '../i18n/use-code-reviewer-setting.i18n';
import { convertToBoolean } from '../utils';

import { INTERNAL_WORKSPACE_UUIDS } from './constants';
import {
  CodeReviewerSetting,
  CodeReviewerSettingContainerType as ContainerType,
  CodeReviewerSettingContainerTypeATI,
  RepositorySettings,
  WorkspaceSettings,
} from './types';

const url = (method: 'get' | 'set') =>
  `/gateway/api/devai-core/public/v2/settings/${method}`;

const isSupportedInternalWorkspaceUUID = (uuid?: string): boolean =>
  INTERNAL_WORKSPACE_UUIDS.includes(uuid ?? '');

// Cache utilities
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
const CACHE_KEY_PREFIX = 'code-reviewer-settings';

type CachedSettings = {
  isEnabledAtWorkspace: boolean;
  isEnabledAtRepository: boolean;
  isAcceptanceCriteriaEnabled: boolean;
  isAcceptanceCriteriaCommitTriggerEnabled: boolean;
};

const getCacheKey = (
  workspaceUuid?: string,
  repositoryUuid?: string
): string => {
  return `${CACHE_KEY_PREFIX}-${workspaceUuid || 'no-workspace'}-${
    repositoryUuid || 'no-repository'
  }`;
};

const getCachedSettings = (
  workspaceUuid?: string,
  repositoryUuid?: string
): CachedSettings | null => {
  if (!store.enabled) return null;

  try {
    const cacheKey = getCacheKey(workspaceUuid, repositoryUuid);
    const cached = store.get(cacheKey);
    return cached || null;
  } catch {
    return null;
  }
};

const setCachedSettings = (
  settings: CachedSettings,
  workspaceUuid?: string,
  repositoryUuid?: string
): void => {
  if (!store.enabled) return;

  try {
    const cacheKey = getCacheKey(workspaceUuid, repositoryUuid);
    const expirationTime = Date.now() + CACHE_DURATION;
    store.setStrictlyNecessaryItem(cacheKey, settings, expirationTime);
  } catch {
    // Silently fail if store is not available
  }
};

const invalidateCache = (
  workspaceUuid?: string,
  repositoryUuid?: string
): void => {
  if (!store.enabled) return;

  try {
    const cacheKey = getCacheKey(workspaceUuid, repositoryUuid);
    store.remove(cacheKey);
  } catch {
    // Silently fail if store is not available
  }
};

type IncompleteBodySetting = {
  containerType: CodeReviewerSettingContainerTypeATI;
  containerId: string;
  parentId?: string; // ARI of the workspace for repositories
  key: 'autoreview_settings';
};
type CodeReviewerBodySetting = IncompleteBodySetting & {
  value:
    | {
        autoreview_enabled: boolean;
        autoreview_acceptance_criteria_enabled: boolean;
        autoreview_acceptance_criteria_commit_trigger_enabled: boolean;
      }
    | {
        autoreview_activated: boolean;
      };
};

type CodeReviewRepositorySettingValues = {
  autoreview_enabled?: string | boolean; // this is from the repo setting
  autoreview_acceptance_criteria_enabled?: string | boolean;
  autoreview_acceptance_criteria_commit_trigger_enabled?: string | boolean;
};

type CodeReviewWorkspaceSettingValues = {
  autoreview_activated: string | boolean; // this is from the workspace setting
};

type CodeReviewerSettingApiResponse = {
  success: boolean;
  settings: {
    containerType: CodeReviewerSettingContainerTypeATI;
    containerId: string;
    key: 'autoreview_settings';
    value: {
      value:
        | CodeReviewRepositorySettingValues
        | CodeReviewWorkspaceSettingValues;
      lastUpdatedTime: Date;
    };
  }[];
};

const body = (
  containerType: ContainerType,
  containerAri: string,
  parentAri?: string,
  settings?: RepositorySettings | WorkspaceSettings
) => {
  let baseSetting: IncompleteBodySetting | CodeReviewerBodySetting = {
    containerType: containerType.ati,
    containerId: containerAri,
    parentId: parentAri,
    key: 'autoreview_settings',
  };

  if (settings) {
    if (containerType === ContainerType.Repository) {
      const {
        isEnabled,
        isAcceptanceCriteriaEnabled,
        isAcceptanceCriteriaCommitTriggerEnabled,
      } = settings as RepositorySettings;
      baseSetting = {
        ...baseSetting,
        value: {
          autoreview_enabled: isEnabled,
          autoreview_acceptance_criteria_enabled: isAcceptanceCriteriaEnabled,
          autoreview_acceptance_criteria_commit_trigger_enabled:
            isAcceptanceCriteriaCommitTriggerEnabled,
        },
      };
    } else if (containerType === ContainerType.Workspace) {
      baseSetting = {
        ...baseSetting,
        value: {
          autoreview_activated: (settings as WorkspaceSettings)
            .autoreview_activated,
        },
      };
    }
  }

  return JSON.stringify({ settings: [baseSetting] });
};

const requestOptions = (
  containerType: ContainerType,
  containerUUID: string,
  parentUUID?: string,
  settings?: RepositorySettings | WorkspaceSettings
): RequestInit => {
  // this replace wouldn't work with a regex (maybe due to curly brace), so had to do a double replace instead
  const trimmedUUID = containerUUID.replace('{', '').replace('}', '');
  const trimmedParentUUID = parentUUID?.replace('{', '').replace('}', '');
  const ARI = `${containerType.ari}/${trimmedUUID}`;
  const parentARI = trimmedParentUUID
    ? `ari:cloud:bitbucket::workspace/${trimmedParentUUID}`
    : undefined;

  return {
    method: 'POST',
    body: body(containerType, ARI, parentARI, settings),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  };
};

const getContainerUUID = (
  containerType: ContainerType,
  repositoryUuid?: string,
  workspaceUuid?: string
): { containerUUID: string | undefined; parentUUID: string | undefined } => {
  switch (containerType) {
    case ContainerType.Repository:
      return { containerUUID: repositoryUuid, parentUUID: workspaceUuid };
    case ContainerType.Workspace:
      return { containerUUID: workspaceUuid, parentUUID: undefined };
  }

  return { containerUUID: undefined, parentUUID: undefined };
};

const getErrorMessage = (
  setting: keyof RepositorySettings,
  value?: boolean
) => {
  switch (setting) {
    case 'isEnabled':
      return value
        ? messages.codeReviewerRepositoryOnError
        : messages.codeReviewerRepositoryOffError;
    case 'isAcceptanceCriteriaEnabled':
      return value
        ? messages.codeReviewerRepositoryAcceptanceCriteriaOnError
        : messages.codeReviewerRepositoryAcceptanceCriteriaOffError;
    case 'isAcceptanceCriteriaCommitTriggerEnabled':
      return value
        ? messages.codeReviewerRepositoryAcceptanceCriteriaCommitTriggerOnError
        : messages.codeReviewerRepositoryAcceptanceCriteriaCommitTriggerOffError;
    default:
      return null;
  }
};

type Props = {
  containerType: ContainerType;
  forceInvalidateCache?: boolean;
};
export const useCodeReviewerSetting = ({
  containerType,
  forceInvalidateCache = false,
}: Props): CodeReviewerSetting => {
  const [error, setError] = useState<FormattedMessage.MessageDescriptor | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEnabledAtWorkspace, setIsEnabledAtWorkspace] =
    useState<boolean>(false);

  const { repository, workspace } = useContext(CurrentDataContext);

  // Feature gate for caching functionality
  const isCachingEnabled = useStatsigGate(
    StatsigFeatureKeys.codeReviewerFrontendStatusCaching
  );

  const blockedRepoSlugs = useConfigStringList(
    StatsigFeatureKeys.autoreviewBlockedRepoSlugs,
    []
  );
  const isRepositoryDisabledForAutoreview =
    (repository?.full_name &&
      blockedRepoSlugs.includes(repository?.full_name)) ||
    false;

  const [repositorySettings, setRepositorySettings] =
    useState<RepositorySettings>({
      isEnabled: false,
      isAcceptanceCriteriaEnabled: true,
      isAcceptanceCriteriaCommitTriggerEnabled: true,
    });

  const getCodeReviewerSetting = useCallback(
    async (
      selectedContainerType: ContainerType
    ): Promise<{ workspace?: boolean }> => {
      setError(null);

      // Only use caching logic if feature gate is enabled
      if (isCachingEnabled) {
        // Check if we should invalidate cache based on force parameter
        if (forceInvalidateCache) {
          invalidateCache(workspace?.uuid, repository?.uuid);
        }

        // Try to get cached settings first (but not if cache invalidation is forced)
        if (!forceInvalidateCache) {
          const cachedSettings = getCachedSettings(
            workspace?.uuid,
            repository?.uuid
          );
          if (cachedSettings) {
            setRepositorySettings({
              isEnabled: cachedSettings.isEnabledAtRepository,
              isAcceptanceCriteriaEnabled:
                cachedSettings.isAcceptanceCriteriaEnabled,
              isAcceptanceCriteriaCommitTriggerEnabled:
                cachedSettings.isAcceptanceCriteriaCommitTriggerEnabled,
            });
            setIsEnabledAtWorkspace(cachedSettings.isEnabledAtWorkspace);
            setIsLoading(false);
            return {
              workspace: cachedSettings.isEnabledAtWorkspace,
            };
          }
        }
      }

      const { containerUUID } = getContainerUUID(
        selectedContainerType,
        repository?.uuid,
        workspace?.uuid
      );

      try {
        if (containerUUID) {
          const res = await fetch(
            authRequest(
              url('get'),
              requestOptions(selectedContainerType, containerUUID)
            )
          );
          if (!res.ok) {
            throw new Error(
              `http error status ${res.status} with text: ${res.statusText}`
            );
          }
          const data = (await res.json()) as CodeReviewerSettingApiResponse;

          const cache: CachedSettings = {
            isEnabledAtRepository: false,
            isEnabledAtWorkspace: false,
            isAcceptanceCriteriaEnabled: true,
            isAcceptanceCriteriaCommitTriggerEnabled: true,
          };

          if (selectedContainerType === ContainerType.Repository) {
            const newRepositorySettings =
              (data.settings[0]?.value
                ?.value as CodeReviewRepositorySettingValues) ?? {};
            const isRepositoryEnabled = convertToBoolean(
              newRepositorySettings.autoreview_enabled,
              isSupportedInternalWorkspaceUUID(workspace?.uuid)
            );
            const isAcceptanceCriteriaEnabled = convertToBoolean(
              newRepositorySettings.autoreview_acceptance_criteria_enabled,
              true
            );
            const isAcceptanceCriteriaCommitTriggerEnabled = convertToBoolean(
              newRepositorySettings.autoreview_acceptance_criteria_commit_trigger_enabled,
              true
            );

            setRepositorySettings({
              isEnabled: isRepositoryEnabled,
              isAcceptanceCriteriaEnabled,
              isAcceptanceCriteriaCommitTriggerEnabled,
            });
            cache.isEnabledAtRepository = isRepositoryEnabled;
            cache.isAcceptanceCriteriaEnabled = isAcceptanceCriteriaEnabled;
            cache.isAcceptanceCriteriaCommitTriggerEnabled =
              isAcceptanceCriteriaCommitTriggerEnabled;

            const workspaceSetting = await getCodeReviewerSetting(
              ContainerType.Workspace
            );
            cache.isEnabledAtWorkspace = workspaceSetting.workspace ?? false;
          } else if (selectedContainerType === ContainerType.Workspace) {
            const workspaceSettings =
              (data.settings[0]?.value
                ?.value as CodeReviewWorkspaceSettingValues) ?? {};
            const isWorkspaceEnabled = convertToBoolean(
              workspaceSettings.autoreview_activated,
              false
            );
            setIsEnabledAtWorkspace(isWorkspaceEnabled);
          }

          // Cache the settings when we have both values (only if caching is enabled)
          if (isCachingEnabled) {
            setCachedSettings(cache, workspace?.uuid, repository?.uuid);
          }

          setIsLoading(false);

          return { workspace: isEnabledAtWorkspace };
        } else {
          throw new Error('Container does not exist');
        }
      } catch {
        setError(messages.codeReviewerGenericGetError);
        setIsLoading(false);
        return {};
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCachingEnabled, repository?.uuid, workspace?.uuid, forceInvalidateCache]
  );

  useEffect(() => {
    getCodeReviewerSetting(containerType);
  }, [getCodeReviewerSetting, containerType]);

  const updateContainerValue = useCallback(
    async (settings: RepositorySettings | WorkspaceSettings) => {
      const { containerUUID, parentUUID } = getContainerUUID(
        containerType,
        repository?.uuid,
        workspace?.uuid
      );

      if (containerUUID) {
        const res = await fetch(
          authRequest(
            url('set'),
            requestOptions(containerType, containerUUID, parentUUID, settings)
          )
        );
        if (!res.ok) {
          throw new Error(
            `http error status ${res.status} with text: ${res.statusText}`
          );
        }

        // Update setting for non-optimistic update
        if (
          containerType === ContainerType.Workspace &&
          'autoreview_activated' in settings
        ) {
          setIsEnabledAtWorkspace(settings.autoreview_activated);
        }

        // Update cache after successful update (only if caching is enabled)
        if (isCachingEnabled) {
          const cacheUpdate: CachedSettings = {
            isEnabledAtRepository: repositorySettings.isEnabled,
            isEnabledAtWorkspace,
            isAcceptanceCriteriaEnabled:
              repositorySettings.isAcceptanceCriteriaEnabled,
            isAcceptanceCriteriaCommitTriggerEnabled:
              repositorySettings.isAcceptanceCriteriaCommitTriggerEnabled,
          };

          if (containerType === ContainerType.Repository) {
            cacheUpdate.isEnabledAtRepository = (
              settings as RepositorySettings
            ).isEnabled;
            cacheUpdate.isAcceptanceCriteriaEnabled = (
              settings as RepositorySettings
            ).isAcceptanceCriteriaEnabled;
            cacheUpdate.isAcceptanceCriteriaCommitTriggerEnabled = (
              settings as RepositorySettings
            ).isAcceptanceCriteriaCommitTriggerEnabled;
          }

          if (containerType === ContainerType.Workspace) {
            cacheUpdate.isEnabledAtWorkspace = (
              settings as WorkspaceSettings
            ).autoreview_activated;
          }

          setCachedSettings(cacheUpdate, workspace?.uuid, repository?.uuid);
        }
      } else {
        throw new Error(`${containerType.name} does not exist`);
      }
    },
    [
      containerType,
      repository?.uuid,
      workspace?.uuid,
      isCachingEnabled,
      repositorySettings,
      isEnabledAtWorkspace,
    ]
  );

  const updateRepositorySetting = useCallback(
    async <K extends keyof RepositorySettings>(
      key: K,
      value: RepositorySettings[K]
    ) => {
      setError(null);
      setRepositorySettings(prev => ({ ...prev, [key]: value }));

      // Prepare values for API
      const apiSettings = { ...repositorySettings, [key]: value };

      try {
        await updateContainerValue(apiSettings);
      } catch {
        setRepositorySettings(prev => ({ ...prev, [key]: !value })); // reverse optimistic update
        setError(getErrorMessage(key, value));
      }
    },
    [repositorySettings, updateContainerValue]
  );

  const updateWorkspaceSetting = useCallback(async () => {
    setError(null);
    const newValue = !isEnabledAtWorkspace;

    // Optimistically update
    setIsEnabledAtWorkspace(newValue);

    try {
      await updateContainerValue({ autoreview_activated: newValue });
    } catch {
      setIsEnabledAtWorkspace(!newValue); // reverse optimistic update
      if (newValue) {
        setError(messages.codeReviewerWorkspaceOnError);
      } else {
        setError(messages.codeReviewerWorkspaceOffError);
      }
    }
  }, [isEnabledAtWorkspace, updateContainerValue]);

  return {
    error,
    isLoading,
    isEnabledAtWorkspace,
    isRepositoryDisabledForAutoreview,
    repositorySettings,
    updateRepositorySetting,
    updateWorkspaceSetting,
  };
};
